import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../app/context/userContext";
import ChatInput from "../common/ChatInput";
import SideMenu from "../common/SideMenu";
import ChatHeader from "../common/ChatHeader";
import ChatWindow from "../chat/ChatWindow.jsx";
import { useAssistants } from "../../app/hooks/useAssistants";
import { useThreads } from "../../app/hooks/useThreads";
import { useChat } from "../../app/hooks/useChat";
import { renameThread, updateUserLanguage } from "../../app/api/apiService";
import i18next from "i18next";
import { jwtDecode } from 'jwt-decode';

export default function MainLayout() {
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const { user, userId, isAdmin, setUser, setUserId, setUserName } = useUser();
  const [sideMenuVisible, setSideMenuVisible] = useState(true);
  const [exploring, setExploring] = useState(true);

  const [lang, setLang] = useState(i18next.language || "en");

  useEffect(() => {
    if (user?.language && user.language !== lang) {
      setLang(user.language);
      i18next.changeLanguage(user.language);
    }
    else if (user?.language && i18next.language !== user.language) {
       i18next.changeLanguage(user.language);
   }
  }, [user?.language]);
  
  useEffect(() => {
    if (i18next.language !== lang) {
      setLang(i18next.language);
    }
    const handler = lng => setLang(lng);
    i18next.on("languageChanged", handler);
    return () => i18next.off("languageChanged", handler);
  }, []);

  const handleLangChange = async (lng) => {
    setLang(lng);
    i18next.changeLanguage(lng);
    if (userId) {
      try {
        await updateUserLanguage(userId, lng);
        const tokenResponse = await fetch('/auth/refresh-token', {
          headers: { Authorization: `Bearer ${localStorage.getItem('userToken')}` }
        });
        const { token: freshToken } = await tokenResponse.json();
        localStorage.setItem('userToken', freshToken);
        const decoded = jwtDecode(freshToken);
        setUser({ loggedIn: true, ...decoded });
      } catch (err) {
        console.error("Failed to save user language:", err);
      }
    }
  };  

  const {
    assistants,
    currentAssistant,
    assistantName,
    hints,
    chooseAssistant,
  } = useAssistants(userId);

  const { threads, deleteThread, addThread, setThreads, refreshThreads } = useThreads(userId);

  const handleRenameThread = async (threadId, newTitle) => {
    await renameThread(threadId, newTitle);
    setThreads(threads => threads.map(t => t.id === threadId ? { ...t, title: newTitle } : t));
  };

  const {
    chatLog,
    chatLogRef,
    currentThread,
    isLoading,
    send,
    selectThread,
    clear,
    setCurrentThread
  } = useChat(currentAssistant, userId);

  const lastInputRef = useRef("");
  const lastAddedThreadIdRef = useRef(null);

  useEffect(() => {
    if (!exploring && chatLogRef && chatLogRef.current) {
      chatLogRef.current.scrollTop = chatLogRef.current.scrollHeight;
    }
  }, [chatLog, currentThread, exploring, chatLogRef]);

  useEffect(() => {
    if (
      currentThread &&
      currentThread.id &&
      !threads.some((t) => t.id === currentThread.id) &&
      lastAddedThreadIdRef.current !== currentThread.id
    ) {
      addThread({
        id: currentThread.id,
        title: lastInputRef.current || "New chat",
        ...(currentThread.assistantId && { assistantId: currentThread.assistantId }),
      });
      lastAddedThreadIdRef.current = currentThread.id;
    }
  }, [currentThread, threads, addThread]);

  const newChat = () => {
    clear();
    setExploring(true);
    setInput("");
    lastAddedThreadIdRef.current = null;
  };

  const handleSelectThread = async (t) => {
    const assistantId = await selectThread(t);
    if (assistantId) {
      const asst = assistants.find((a) => a.assistantId === assistantId);
      if (asst) chooseAssistant(asst);
      setExploring(false);
      setInput("");
      lastAddedThreadIdRef.current = t.id;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    setUser(null);
    setUserId(null);
    setUserName(null);
  };

  const handleChooseAssistant = (a) => {
    chooseAssistant(a);
    clear();
    setExploring(false);
    setInput("");
    lastAddedThreadIdRef.current = null;
    if (typeof setCurrentThread === "function") setCurrentThread(null);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    lastInputRef.current = trimmed;

    await send(trimmed);
    await refreshThreads();
    setInput("");
  }

  return (
    <div className="App">
      <SideMenu
        visible={sideMenuVisible}
        threads={threads}
        onNewChat={newChat}
        onSelectThread={handleSelectThread}
        onDeleteThread={deleteThread}
        onRenameThread={handleRenameThread}
        onLogout={handleLogout}
        onAdmin={() => navigate("/admin")}
        isAdmin={isAdmin}
      />

      <div className="main-content">
        <ChatHeader
          sideMenuVisible={sideMenuVisible}
          toggleSideMenu={() => setSideMenuVisible((v) => !v)}
          assistantName={assistantName}
          exploring={exploring}
          currentLang={lang}
          onLangChange={handleLangChange}
        />

        <ChatWindow
          exploring={exploring}
          assistants={assistants}
          chooseAssistant={handleChooseAssistant}
          chatLog={chatLog}
          chatLogRef={chatLogRef}
          hints={hints}
          setInput={setInput}
          handleSubmit={handleSubmit}
          currentLang={lang}
        />

        {!exploring && (
          <ChatInput
            sideMenuVisible={sideMenuVisible}
            isLoading={isLoading}
            setInput={setInput}
            input={input}
            handleSubmit={handleSubmit}
            currentLang={lang}
          />
        )}
      </div>
    </div>
  );
}